// Super Admin Page - Monitor all customers and their plans
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  AppBar,
  Toolbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Grid,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  Edit,
  Business,
  People,
  AttachMoney,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useToast } from '@/contexts/ToastContext';
import type { Organization, PricingPlan } from '@/types';
import { PLAN_LIMITS, PLAN_PRICING, NZ_GST_RATE } from '@/types';

const PLAN_COLORS: Record<PricingPlan, 'default' | 'primary' | 'secondary' | 'success'> = {
  free: 'default',
  pro: 'primary',
  business: 'secondary',
  enterprise: 'success',
};

export const SuperAdminPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);
  const [editPlan, setEditPlan] = useState<PricingPlan>('free');

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'organizations'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const orgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Organization[];
      setOrganizations(orgs);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      showError('Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleEditOpen = (org: Organization) => {
    setEditOrg(org);
    setEditPlan(org.plan);
  };

  const handleEditClose = () => {
    setEditOrg(null);
  };

  const handlePlanUpdate = async () => {
    if (!editOrg) return;

    try {
      await updateDoc(doc(db, 'organizations', editOrg.id), {
        plan: editPlan,
        limits: PLAN_LIMITS[editPlan],
        updatedAt: serverTimestamp(),
      });

      setOrganizations((prev) =>
        prev.map((org) =>
          org.id === editOrg.id
            ? { ...org, plan: editPlan, limits: PLAN_LIMITS[editPlan] }
            : org
        )
      );

      showSuccess(`Plan updated to ${editPlan} for ${editOrg.name}`);
      handleEditClose();
    } catch (err) {
      console.error('Error updating plan:', err);
      showError('Failed to update plan');
    }
  };

  const calculateMonthlyRevenue = (org: Organization): number => {
    const basePricePerSeat = PLAN_PRICING[org.plan].monthly;
    const seats = org.usage.seatCount || 1;
    let total = basePricePerSeat * seats;

    // Add NZ GST if applicable
    if (org.isNZBased && org.plan !== 'free') {
      total = total * (1 + NZ_GST_RATE);
    }

    return total;
  };

  const totalMonthlyRevenue = organizations.reduce(
    (sum, org) => sum + calculateMonthlyRevenue(org),
    0
  );

  const orgsByPlan = organizations.reduce(
    (acc, org) => {
      acc[org.plan] = (acc[org.plan] || 0) + 1;
      return acc;
    },
    {} as Record<PricingPlan, number>
  );

  if (loading) {
    return <LoadingSpinner fullPage message="Loading organizations..." />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" color="secondary">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Super Admin Dashboard
          </Typography>
          <Button
            color="inherit"
            startIcon={<Refresh />}
            onClick={fetchOrganizations}
          >
            Refresh
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Business color="primary" sx={{ mr: 1 }} />
                  <Typography color="text.secondary">
                    Total Organizations
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {organizations.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <People color="primary" sx={{ mr: 1 }} />
                  <Typography color="text.secondary">Paid Customers</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {organizations.filter((o) => o.plan !== 'free').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoney color="primary" sx={{ mr: 1 }} />
                  <Typography color="text.secondary">Est. MRR</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  ${(totalMonthlyRevenue / 100).toFixed(0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  By Plan
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(orgsByPlan).map(([plan, count]) => (
                    <Chip
                      key={plan}
                      label={`${plan}: ${count}`}
                      size="small"
                      color={PLAN_COLORS[plan as PricingPlan]}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Organizations Table */}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          All Organizations
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Organization</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Seats</TableCell>
                <TableCell>Projects</TableCell>
                <TableCell>Est. Revenue</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell>
                    <Typography fontWeight="medium">{org.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {org.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>{org.billingEmail}</TableCell>
                  <TableCell>
                    <Chip
                      label={org.plan.toUpperCase()}
                      size="small"
                      color={PLAN_COLORS[org.plan]}
                    />
                  </TableCell>
                  <TableCell>
                    {org.billingCountry}
                    {org.isNZBased && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        +15% GST
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{org.usage.seatCount}</TableCell>
                  <TableCell>{org.usage.projectCount}</TableCell>
                  <TableCell>
                    ${(calculateMonthlyRevenue(org) / 100).toFixed(2)}/mo
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditOpen(org)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {organizations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No organizations yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Stripe Integration Note */}
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Stripe Integration (Stub)
          </Typography>
          <Typography variant="body2">
            Stripe billing integration is stubbed. Configure your Stripe keys in
            the environment variables and Firebase Functions to enable automatic
            billing.
          </Typography>
        </Alert>
      </Container>

      {/* Edit Plan Dialog */}
      <Dialog open={!!editOrg} onClose={handleEditClose}>
        <DialogTitle>Edit Organization Plan</DialogTitle>
        <DialogContent sx={{ pt: 2, minWidth: 400 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Organization: <strong>{editOrg?.name}</strong>
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Plan</InputLabel>
            <Select
              value={editPlan}
              label="Plan"
              onChange={(e) => setEditPlan(e.target.value as PricingPlan)}
            >
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="pro">Pro ($5-7/seat)</MenuItem>
              <MenuItem value="business">Business ($12-15/seat)</MenuItem>
              <MenuItem value="enterprise">Enterprise (Custom)</MenuItem>
            </Select>
          </FormControl>

          {editOrg?.isNZBased && editPlan !== 'free' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              15% GST will be applied for this NZ-based organization.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handlePlanUpdate} variant="contained">
            Update Plan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
